import {
  Button,
  ModuleIcon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@berky/ui'
import {
  Bell,
  ChartColumn,
  ChevronUp,
  LayoutGrid,
  Menu,
  MessageSquareText,
  Search,
  Settings,
  Users,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'

import { Brand } from './brand'
import { NavigationPanel } from './navigation-panel'
import { SystemEnvironmentBadge } from './system-environment-badge'
import { findModule } from '../modules'

const SUCHE_IN_VORBEREITUNG = 'Suche über alle Anwendungen — in Vorbereitung'

/**
 * Kopfzeile der Plattform — bewusst zurückhaltend.
 *
 * Sie ist 48px hoch und trägt nur, was die Plattform selbst ausmacht: den
 * Zugang zur Navigation, den Namen, die Suche und das Nutzermenü. Der
 * Modulname sagt, wo man ist; alles Weitere gehört dem Modul. Die
 * Plattformnavigation soll hier zurücktreten, damit ein Modul seine eigene
 * Navigation in den Vordergrund stellen kann.
 *
 * Die Marke steht auf jeder Route an derselben Stelle und führt immer zur
 * Startseite. Der separate Menüknopf folgt ihr und öffnet die
 * Plattformnavigation als vollbreite Fläche unterhalb der Kopfzeile. Die
 * Fläche liegt über dem Seiteninhalt, statt ihn zu verschieben. Dieses Muster
 * gilt auf der Startseite und in den Modulen gleichermaßen.
 */
export function Header({ children }: { children?: React.ReactNode }) {
  const [navOpen, setNavOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const modul = aktuellesModul(location.pathname)
  const plattformfunktion = aktuellePlattformfunktion(location.pathname)

  useEffect(() => {
    if (!navOpen) return

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setNavOpen(false)
        triggerRef.current?.focus()
      }
    }

    function closeOutside(event: PointerEvent) {
      const target = event.target
      if (!(target instanceof Node)) return
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return
      }
      setNavOpen(false)
    }

    document.addEventListener('keydown', closeOnEscape)
    document.addEventListener('pointerdown', closeOutside)
    return () => {
      document.removeEventListener('keydown', closeOnEscape)
      document.removeEventListener('pointerdown', closeOutside)
    }
  }, [navOpen])

  return (
    <>
      <Brand className="shrink-0" wordmarkClassName="hidden sm:flex" />
      <SystemEnvironmentBadge />

      <Button
        ref={triggerRef}
        variant="ghost"
        size="icon"
        className="size-9 shrink-0"
        aria-label={navOpen ? 'Navigation schließen' : 'Navigation öffnen'}
        aria-expanded={navOpen}
        aria-controls="platform-navigation-panel"
        onClick={() => setNavOpen((open) => !open)}
      >
        {navOpen ? (
          <ChevronUp className="text-muted-foreground" aria-hidden="true" />
        ) : (
          <Menu className="text-muted-foreground" aria-hidden="true" />
        )}
      </Button>

      {navOpen && (
        <div ref={panelRef} className="contents">
          <NavigationPanel onNavigate={() => setNavOpen(false)} />
        </div>
      )}

      {(modul || plattformfunktion) && (
        <div
          className="flex min-w-0 items-center gap-2"
          aria-label={
            plattformfunktion
              ? `Plattformfunktion: ${plattformfunktion.title}`
              : undefined
          }
        >
          {/* Auf schmalen Bildschirmen bleibt nur das Markenzeichen sichtbar;
              dort entfällt der Trenner zugunsten des Modulnamens. */}
          <span aria-hidden="true" className="hidden text-border sm:inline">
            /
          </span>

          {plattformfunktion && (
            <>
              <span className="hidden shrink-0 text-xs font-medium text-muted-foreground sm:inline">
                Plattform
              </span>
              <span aria-hidden="true" className="hidden text-border sm:inline">
                /
              </span>
            </>
          )}

          <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
            {modul ? (
              <ModuleIcon name={modul.icon} className="size-4 shrink-0" />
            ) : (
              plattformfunktion && (
                <plattformfunktion.Icon className="size-4 shrink-0" aria-hidden="true" />
              )
            )}
            <span className="truncate">{modul?.title ?? plattformfunktion?.title}</span>
          </span>
        </div>
      )}

      <div className="ml-auto flex items-center gap-1">
        {/* Explain the unavailable search to keyboard and pointer users. */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-9 text-muted-foreground"
                aria-disabled="true"
                title={SUCHE_IN_VORBEREITUNG}
                aria-label={SUCHE_IN_VORBEREITUNG}
              >
                <Search aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Die anwendungsübergreifende Suche ist in Vorbereitung.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {children}
      </div>
    </>
  )
}

/**
 * Das Modul zur aktuellen Adresse, oder `undefined` außerhalb eines Moduls.
 */
function aktuellesModul(pathname: string) {
  const ersterAbschnitt = pathname.split('/').filter(Boolean)[0]
  if (!ersterAbschnitt) return undefined
  return findModule(decodeURIComponent(ersterAbschnitt))
}

const PLATTFORMFUNKTIONEN = [
  { path: 'einstellungen', title: 'Einstellungen', Icon: Settings },
  { path: 'nutzungsanalyse', title: 'Nutzungsanalyse', Icon: ChartColumn },
  { path: 'benachrichtigungen', title: 'Benachrichtigungen', Icon: Bell },
  {
    path: 'module-navigation',
    title: 'Module und Navigation',
    Icon: LayoutGrid,
  },
  {
    path: 'verwaltung',
    title: 'Nutzer und Rollen',
    Icon: Users,
  },
  {
    path: 'feedback',
    title: 'Feedback',
    Icon: MessageSquareText,
  },
  {
    path: 'mein-feedback',
    title: 'Mein Feedback',
    Icon: MessageSquareText,
  },
] as const

/** Die Plattformfunktion zur aktuellen Adresse einschließlich Unterseiten. */
function aktuellePlattformfunktion(pathname: string) {
  const ersterAbschnitt = pathname.split('/').filter(Boolean)[0]
  if (!ersterAbschnitt) return undefined
  const path = decodeURIComponent(ersterAbschnitt)
  return PLATTFORMFUNKTIONEN.find((funktion) => funktion.path === path)
}
